import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import type { Stage } from './stageStore';

/**
 * `statut` dit ce que le centre est POUR LE PUBLIC ; `active` et `archived`
 * disent s'il figure dans les listes de travail de l'administration. Les deux
 * questions sont distinctes, et c'est voulu : seul `statut` decide de ce que
 * le site montre.
 */
export type StatutDeCentre = 'actif' | 'pause' | 'test';

interface Center {
  id: string;
  name: string;
  address: string;
  postal_code: string;
  city: string;
  tag: string;
  active: boolean;
  archived: boolean;
  archived_at?: string;
  phone?: string;
  address2?: string;
  statut?: StatutDeCentre;
  /* Après la migration des journées pédagogiques (20261019150000). */
  centre_de_vacances?: boolean;
  school_id?: string | null;
}


interface Session {
  id: string;
  stage_id: string;
  center_id: string;
  periode: string;
  start_date: string;
  end_date: string;
  capacity: number;
  active: boolean;
  archived: boolean;
  archived_at?: string;
  stage?: Omit<Stage, 'nb_sessions' | 'nb_sessions_a_venir'>;
  center?: Center;
  nombre_jours?: number;
  semaine?: string;
  prix_normal: number;
  prix_reduit?: number;
  prix_local?: number;
  prix_local_reduit?: number;
  remarques?: string;
  tarif_condition_id?: string;
  visible_from?: string;
  current_registrations: number;
}

interface AdminState {
  centers: Center[];
  sessions: Session[];
  isLoading: boolean;
  error: string | null;
  fetchCenters: (includeArchived?: boolean) => Promise<void>;
  archiveCenter: (id: string) => Promise<void>;
  unarchiveCenter: (id: string) => Promise<void>;
  fetchSessions: (includeArchived?: boolean) => Promise<void>;
  archiveSession: (id: string) => Promise<void>;
  unarchiveSession: (id: string) => Promise<void>;
  createCenter: (center: Omit<Center, 'id' | 'created_at'>) => Promise<void>;
  updateCenter: (id: string, updates: Partial<Center>) => Promise<void>;
  deleteCenter: (id: string) => Promise<void>;
  createSession: (session: Omit<Session, 'id' | 'created_at'>) => Promise<void>;
  updateSession: (id: string, updates: Partial<Session>) => Promise<void>;
  deleteSession: (id: string) => Promise<void>;
}

export const useAdminStore = create<AdminState>((set, get) => ({
  centers: [],
  sessions: [],
  isLoading: false,
  error: null,

  fetchCenters: async (includeArchived = false) => {
    set({ isLoading: true, error: null });
    try {
      let query = supabase.from('centers').select('*').order('name');
      if (!includeArchived) {
        query = query.eq('archived', false);
      }
      const { data, error } = await query;
      if (error) throw error;
      set({ centers: data || [] });
    } catch (error: any) {
      set({ error: error.message });
    } finally {
      set({ isLoading: false });
    }
  },

  archiveCenter: async (id) => {
    try {
      const archivedAt = new Date().toISOString();
      const { error } = await supabase
        .from('centers')
        .update({ archived: true, archived_at: archivedAt })
        .eq('id', id);
      if (error) throw error;
      set(state => ({
        centers: state.centers.map(c =>
          c.id === id ? { ...c, archived: true, archived_at: archivedAt } : c
        )
      }));
    } catch (error: any) {
      set({ error: error.message });
      throw error;
    }
  },

  unarchiveCenter: async (id) => {
    try {
      const { error } = await supabase
        .from('centers')
        .update({ archived: false, archived_at: null })
        .eq('id', id);
      if (error) throw error;
      set(state => ({
        centers: state.centers.map(c =>
          c.id === id ? { ...c, archived: false, archived_at: undefined } : c
        )
      }));
    } catch (error: any) {
      set({ error: error.message });
      throw error;
    }
  },

  fetchSessions: async (includeArchived = false) => {
    set({ isLoading: true, error: null });
    try {
      let query = supabase
        .from('sessions')
        .select(`
          id,
          stage_id,
          center_id,
          periode,
          start_date,
          end_date,
          capacity,
          prix_normal,
          prix_reduit,
          tarif_condition_id,
          prix_local,
          nombre_jours,
          prix_local_reduit,
          remarques,
          active,
          archived,
          archived_at,
          visible_from,
          semaine,
          current_registrations,
          stage:stages(*),
          center:centers(*)
        `)
        .order('start_date');

      if (!includeArchived) {
        query = query.eq('archived', false);
      }

      const { data, error } = await query;
      if (error) throw error;

      set({ sessions: data || [] });
    } catch (error: any) {
      set({ error: error.message });
    } finally {
      set({ isLoading: false });
    }
  },

  archiveSession: async (id) => {
    try {
      const archivedAt = new Date().toISOString();
      const { error } = await supabase
        .from('sessions')
        .update({ archived: true, archived_at: archivedAt })
        .eq('id', id);

      if (error) throw error;
      set(state => ({
        sessions: state.sessions.map(s =>
          s.id === id ? { ...s, archived: true, archived_at: archivedAt } : s
        )
      }));
    } catch (error: any) {
      set({ error: error.message });
      throw error;
    }
  },

  unarchiveSession: async (id) => {
    try {
      const { error } = await supabase
        .from('sessions')
        .update({ archived: false, archived_at: null })
        .eq('id', id);

      if (error) throw error;
      set(state => ({
        sessions: state.sessions.map(s =>
          s.id === id ? { ...s, archived: false, archived_at: undefined } : s
        )
      }));
    } catch (error: any) {
      set({ error: error.message });
      throw error;
    }
  },

  createCenter: async (center) => {
    set({ isLoading: true, error: null });
    try {
      const { error } = await supabase
        .from('centers')
        .insert([center]);

      if (error) throw error;
      await get().fetchCenters();
    } catch (error: any) {
      set({ error: error.message });
    } finally {
      set({ isLoading: false });
    }
  },

  updateCenter: async (id, updates) => {
    set({ isLoading: true, error: null });
    try {
      const { error } = await supabase
        .from('centers')
        .update(updates)
        .eq('id', id);

      if (error) throw error;
      await get().fetchCenters();
    } catch (error: any) {
      set({ error: error.message });
    } finally {
      set({ isLoading: false });
    }
  },

  deleteCenter: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const { error } = await supabase
        .from('centers')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await get().fetchCenters();
    } catch (error: any) {
      set({ error: error.message });
    } finally {
      set({ isLoading: false });
    }
  },

  createSession: async (session) => {
    set({ isLoading: true, error: null });
    try {
      const { error } = await supabase
        .from('sessions')
        .insert([session]);

      if (error) throw error;
      await get().fetchSessions();
    } catch (error: any) {
      set({ error: error.message });
    } finally {
      set({ isLoading: false });
    }
  },

  updateSession: async (id, updates) => {
    set({ isLoading: true, error: null });
    try {
      const { error } = await supabase
        .from('sessions')
        .update(updates)
        .eq('id', id);

      if (error) throw error;
      await get().fetchSessions();
    } catch (error: any) {
      set({ error: error.message });
    } finally {
      set({ isLoading: false });
    }
  },

  deleteSession: async (id) => {
    try {
      const { error } = await supabase
        .from('sessions')
        .delete()
        .eq('id', id);

      if (error) throw error;
      set(state => ({
        sessions: state.sessions.filter(s => s.id !== id)
      }));
    } catch (error: any) {
      set({ error: error.message });
      throw error;
    }
  },
}));